import type { ScheduleSpec } from '../../contracts/routines.types';
import type { ScheduleIntl } from '../../contracts/workflows/lib/schedule-intl';
import {
  formatWeekdayLabel,
  parseWeekdayCron,
} from '../../contracts/workflows/lib/weekday-cron';

function formatTimeOfDay(
  intl: ScheduleIntl,
  isoString: string,
  timeZone: string
): string {
  try {
    return intl.formatTime(new Date(isoString), {
      hour: 'numeric',
      minute: '2-digit',
      timeZone,
    });
  } catch {
    return '';
  }
}

function resolveTimeZone(intl: ScheduleIntl, timezone: string | undefined): string {
  return timezone || intl.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
}

function dayOfWeekInTimeZone(isoString: string, timeZone: string): number | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      weekday: 'short',
    }).formatToParts(new Date(isoString));
    const weekday = parts.find((part) => part.type === 'weekday')?.value ?? '';
    const dayByWeekday: Record<string, number> = {
      Sun: 0,
      Mon: 1,
      Tue: 2,
      Wed: 3,
      Thu: 4,
      Fri: 5,
      Sat: 6,
    };
    return dayByWeekday[weekday] ?? null;
  } catch {
    return null;
  }
}

function formatWeeklyDayLabel(
  intl: ScheduleIntl,
  isoString: string,
  timeZone: string
): string {
  const day = dayOfWeekInTimeZone(isoString, timeZone);
  if (day === null) {
    return intl.formatMessage({
      defaultMessage: 'Weekly',
      id: 'nfvTy0RZMv',
      description: 'Fallback cadence for a weekly routine schedule',
    });
  }
  return formatWeekdayLabel(intl, [day]);
}

/**
 * Details / Before-After cadence, e.g. "Daily at 7:00 AM". Distinct from
 * overview chips (`Daily · 8:00 AM`) on purpose.
 */
export function formatRoutineScheduleLabel(
  intl: ScheduleIntl,
  spec: ScheduleSpec
): string {
  const timeZone = resolveTimeZone(intl, spec.timezone);
  const time = formatTimeOfDay(intl, spec.start_at, timeZone);

  switch (spec.frequency.kind) {
    case 'hourly':
      return intl.formatMessage({
        defaultMessage: 'Hourly',
        id: 'AWpZv89lv2',
        description: 'Cadence label for a routine that runs every hour',
      });
    case 'daily':
      return time
        ? intl.formatMessage(
            {
              defaultMessage: 'Daily at {time}',
              id: 'CXiUlVWOT4',
              description: 'Card cadence for a daily routine with its run time',
            },
            { time }
          )
        : intl.formatMessage({
            defaultMessage: 'Daily',
            id: 'C1imfP3/Gj',
            description: 'Cadence label for a routine that runs daily',
          });
    case 'weekly': {
      const day = formatWeeklyDayLabel(intl, spec.start_at, timeZone);
      return time
        ? intl.formatMessage(
            {
              defaultMessage: '{day} at {time}',
              id: '7WokTM0gOQ',
              description: 'Card cadence with a weekday and run time',
            },
            { day, time }
          )
        : day;
    }
    case 'cron': {
      const parsed = parseWeekdayCron(spec.frequency.cron_expression);
      if (parsed) {
        const days = formatWeekdayLabel(intl, parsed.days);
        return time
          ? intl.formatMessage(
              {
                defaultMessage: '{days} at {time}',
                id: 'IiNW5356Bv',
                description: 'Card cadence with scheduled weekdays and run time',
              },
              { days, time }
            )
          : days;
      }
      return intl.formatMessage({
        defaultMessage: 'Scheduled',
        id: 'YtTcweqmer',
        description: 'Fallback label when the next routine run cannot be determined',
      });
    }
    default:
      return intl.formatMessage({
        defaultMessage: 'Scheduled',
        id: 'YtTcweqmer',
        description: 'Fallback label when the next routine run cannot be determined',
      });
  }
}

/** Create-card summary sentence, e.g. "Runs daily at 7:00 AM". */
export function formatRoutineScheduleSentence(
  intl: ScheduleIntl,
  spec: ScheduleSpec
): string {
  const timeZone = resolveTimeZone(intl, spec.timezone);
  const time = formatTimeOfDay(intl, spec.start_at, timeZone);

  switch (spec.frequency.kind) {
    case 'hourly':
      return intl.formatMessage({
        defaultMessage: 'Runs hourly',
        id: 't4/9MLRN3h',
        description: 'Create-routine summary when the proposed schedule is hourly',
      });
    case 'daily':
      return time
        ? intl.formatMessage(
            {
              defaultMessage: 'Runs daily at {time}',
              id: 'BTAxZegh/X',
              description: 'Create-routine summary for a daily schedule',
            },
            { time }
          )
        : intl.formatMessage({
            defaultMessage: 'Runs daily',
            id: 'QAk9xIaEAN',
            description: 'Create-routine summary for a daily schedule without a time',
          });
    case 'weekly': {
      const day = formatWeeklyDayLabel(intl, spec.start_at, timeZone);
      return time
        ? intl.formatMessage(
            {
              defaultMessage: 'Runs {day} at {time}',
              id: 'UTN/qSmHvi',
              description: 'Create-routine summary for a weekly schedule',
            },
            { day, time }
          )
        : intl.formatMessage(
            {
              defaultMessage: 'Runs {day}',
              id: '8RpNO3reb/',
              description: 'Create-routine summary for a weekly schedule without a time',
            },
            { day }
          );
    }
    case 'cron': {
      const parsed = parseWeekdayCron(spec.frequency.cron_expression);
      if (parsed) {
        const days = formatWeekdayLabel(intl, parsed.days);
        return time
          ? intl.formatMessage(
              {
                defaultMessage: 'Runs {days} at {time}',
                id: 'wUdz2dNHox',
                description: 'Create-routine summary for a weekday cron schedule',
              },
              { days, time }
            )
          : intl.formatMessage(
              {
                defaultMessage: 'Runs {days}',
                id: '7oWC3EFMLy',
                description: 'Create-routine summary for a weekday cron without a time',
              },
              { days }
            );
      }
      return intl.formatMessage({
        defaultMessage: 'Runs on a schedule',
        id: 'UfiwVFjIH+',
        description: 'Create-routine summary when the cadence cannot be named',
      });
    }
    default:
      return intl.formatMessage({
        defaultMessage: 'Runs on a schedule',
        id: 'UfiwVFjIH+',
        description: 'Create-routine summary when the cadence cannot be named',
      });
  }
}
